import { Injectable, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { authConfig } from './auth.config';
import { DataService } from './services/data.service';
import { firstValueFrom, timer } from 'rxjs';
import { Router } from '@angular/router';

const LOGOUT_TIMEOUT_MS = 8000;

function localLogoutCleanup() {
  try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('id_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('expires_at');
      sessionStorage.removeItem('discovery_document');
  } catch {}
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private oauthService = inject(OAuthService);
  private dataService = inject(DataService);
  private router = inject(Router);

  constructor() {
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage); // Speicherort explizit setzen
  }

  initLogin() {
    this.oauthService.initLoginFlow();
  }

  logout_old() {
    this.oauthService.logOut();
    this.dataService.isAuthenticated();
  }

  async logout(): Promise<void> {
    try {
      // WICHTIG: Tokens hier NICHT löschen (id_token_hint würde fehlen)
      const issuer = (this.oauthService.issuer ?? '').replace(/\/$/, '');
      const clientId = (this.oauthService as any).clientId ?? 'spa-client-id';
      const idToken = this.oauthService.getIdToken(); // für id_token_hint
      const postLogoutRedirectUri = `${location.origin}/logout-callback`;

      // Primär: Bibliotheks-Logout (hängt id_token_hint selbst an)
      const primary = (async () => {
        await this.oauthService.logOut({ postLogoutRedirectUri });
      })();

      // Fallback: Eigene URL bauen (mit id_token_hint ODER client_id)
      const fallback = (async () => {
        await firstValueFrom(timer(LOGOUT_TIMEOUT_MS));
        const query = idToken
          ? `id_token_hint=${encodeURIComponent(idToken)}`
          : `client_id=${encodeURIComponent(clientId)}`;
        const url =
          `${issuer}/protocol/openid-connect/logout?${query}` +
          `&post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;
        window.location.href = url;
      })();

      await Promise.race([primary, fallback]);
    } catch (e) {
      // Letzte Rettung: lokal aufräumen und zur Startseite
      localLogoutCleanup();
      this.router.navigateByUrl('/');
    }
  }

  handleLoginCallback(): Promise<void> {
    return this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      this.dataService.isAuthenticated();
    });
  }

  isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getIdentityClaims(): any {
    return this.oauthService.getIdentityClaims();
  }
}
